import React from 'react';
import '../../styles/components.css';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error' | 'destructive' | 'default';

export interface AlertProps {
  /**
   * Alert type variant
   * @default 'info'
   */
  variant?: AlertVariant;
  
  /**
   * Alert content
   */
  children: React.ReactNode;
  
  /**
   * Optional alert title
   */
  title?: React.ReactNode;
  
  /**
   * Optional icon to display
   */
  icon?: React.ReactNode;
  
  /**
   * Whether the alert can be dismissed
   * @default false
   */
  dismissible?: boolean;
  
  /**
   * Callback for when the alert is dismissed
   */
  onDismiss?: () => void;
  
  /**
   * Additional CSS class names
   */
  className?: string;
  
  /**
   * Additional props to pass to the div element
   */
  [key: string]: any;
}

/**
 * Alert component for feedback messages
 */
export const Alert = ({
  variant = 'info',
  children,
  title,
  icon,
  dismissible = false,
  onDismiss,
  className = '',
  ...props
}: AlertProps) => {
  // Compute class names
  const baseClass = 'alert';
  const variantClass = `alert-${variant}`;
  
  const classes = [
    baseClass,
    variantClass,
    className
  ].filter(Boolean).join(' ');

  // Default icons based on variant
  const getDefaultIcon = () => {
    switch (variant) {
      case 'info':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      case 'success':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
      case 'destructive':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={classes} role="alert" {...props}>
      {(icon || getDefaultIcon()) && (
        <div className="alert-icon">
          {icon || getDefaultIcon()}
        </div>
      )}
      
      <div className="alert-content">
        {title && (
          <div className="alert-title">{title}</div>
        )}
        <div>{children}</div>
      </div>
      
      {dismissible && onDismiss && (
        <button 
          type="button" 
          className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-500" 
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Alert;

/**
 * Alert Description component for alert content
 */
export function AlertDescription({
  children,
  className = '',
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`alert-description ${className}`} {...props}>
      {children}
    </div>
  );
};